import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDoubleUp } from "@fortawesome/free-solid-svg-icons";

export default function BackToTop() {
  const [isVisible, setIsVisible] = useState(false);
  const [bottomOffset, setBottomOffset] = useState(null);

  useEffect(() => {
    const SCROLL_THRESHOLD = 300;
    const BUTTON_HEIGHT = 48;
    const PADDING = 16;
    const FALLBACK_OFFSET = 50;
    const MOBILE_BREAKPOINT = 1024;
    const CHECK_INTERVAL = 1000;

    const isAuctionPage = () => window.location.pathname.startsWith('/auction/');

    const toggleVisibility = () => {
      setIsVisible(window.pageYOffset > SCROLL_THRESHOLD);
    };

    const updateButtonPosition = () => {
      const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
      
      if (!isMobile) {
        setBottomOffset(null);
        return;
      }

      if (isAuctionPage()) {
        const biddingComponent = document.querySelector('.bidding-component');
        if (biddingComponent) {
          const offset = biddingComponent.getBoundingClientRect().height + BUTTON_HEIGHT + PADDING;
          setBottomOffset(offset);
        } else {
          setBottomOffset(FALLBACK_OFFSET);
        }
      } else {
        setBottomOffset(FALLBACK_OFFSET);
      }
    };

    let resizeObserver = null;

    const setupResizeObserver = () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
      }

      const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
      if (isAuctionPage() && isMobile) {
        const biddingComponent = document.querySelector('.bidding-component');
        if (biddingComponent && window.ResizeObserver) {
          resizeObserver = new ResizeObserver(updateButtonPosition);
          resizeObserver.observe(biddingComponent);
        }
      }
    };

    // Initial setup
    toggleVisibility();
    updateButtonPosition();
    setupResizeObserver();

    // Event listeners
    window.addEventListener("scroll", toggleVisibility, { passive: true });
    window.addEventListener("resize", updateButtonPosition);

    // Periodic check to catch route changes and component updates
    const checkInterval = setInterval(() => {
      const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
      if (isAuctionPage() && isMobile) {
        setupResizeObserver();
        updateButtonPosition();
      } else if (!isAuctionPage()) {
        // Not on auction page - reset to standard position
        if (isMobile) {
          setBottomOffset(FALLBACK_OFFSET);
        } else {
          setBottomOffset(null);
        }
        if (resizeObserver) {
          resizeObserver.disconnect();
          resizeObserver = null;
        }
      }
    }, CHECK_INTERVAL);

    return () => {
      if (checkInterval) clearInterval(checkInterval);
      window.removeEventListener("scroll", toggleVisibility);
      window.removeEventListener("resize", updateButtonPosition);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, []);

  const scrollToTop = () => {
    // Use instant scroll on mobile auction pages to avoid conflicts with window-based list virtualization.
    // Smooth scrolling causes the virtualizer to recalculate mid-scroll, shifting the document height.
    const isAuction = window.location.pathname.startsWith("/auction/");
    const isMobile = window.innerWidth < 1024;
    window.scrollTo({ top: 0, behavior: isAuction && isMobile ? "instant" : "smooth" });
  };

  return (
    <>
      {isVisible && (
        <button
          onClick={scrollToTop}
          style={bottomOffset !== null ? { bottom: `${bottomOffset}px` } : undefined}
          className="fixed right-4 sm:right-8 lg:bottom-8 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg transition-all duration-300 hover:bg-blue-700 active:bg-blue-800 hover:scale-110 active:scale-95 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 touch-manipulation"
          aria-label="Back to top"
        >
          <FontAwesomeIcon icon={faAngleDoubleUp} className="h-5 w-5" />
        </button>
      )}
    </>
  );
}
